import React, { useEffect, useMemo, useRef, useState } from "react";
import type { TPort, TBunkerPrice, TWeather, TCurrentVessel } from "@/types";
import type { TPortNote } from "@/types";
import ApiClient from "../services/fuelandmoreapi";
import { config } from "../config";
import { getForecastColorsFromWeatherHours } from "../utilities/weatherUtils";
import { getWeatherForPortCached } from "../services/weatherService";
import RequestForFirm from "./RequestForFirm";
import PortWeatherForecast from "./PortWeatherForecast";

interface BunkerPriceGridProps {
  ports: TPort[]; // visible ports (deduped) associated with visible routes
  prices: TBunkerPrice[]; // all prices (we'll filter per port)
  getPricesForPort: (unlocode: string) => TBunkerPrice[];
  vessel: TCurrentVessel | null;
  fromPort: string;
  toPort: string;
  eta: string;
  onClose: () => void;
}

// Small helper to render colored forecast rectangles
const ForecastStripe: React.FC<{ colors: string[]; title: string }> = ({
  colors,
  title,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      cursor: colors.length ? "pointer" : "default",
      width: "100%",
      height: 16,
      gap: 0,
    }}
    title={title}
  >
    {colors.length === 0 ? (
      <span
        style={{
          fontSize: 10,
          opacity: 0.6,
          width: "100%",
          textAlign: "center",
        }}
      >
        n/a
      </span>
    ) : (
      colors.map((c, i) => (
        <span
          key={i}
          style={{
            flex: 1,
            height: 14,
            background: c,
            borderRadius: 2,
            marginRight: i === colors.length - 1 ? 0 : 1,
          }}
        />
      ))
    )}
  </div>
);

// Build a map of latest price per (port, grade)
function computeLatestPrices(prices: TBunkerPrice[]) {
  const latest: Record<string, TBunkerPrice> = {};
  for (const p of prices) {
    const key = `${p.bunkerId.toLowerCase()}__${p.fuelGrade.toLowerCase()}`;
    if (
      !latest[key] ||
      new Date(p.timestamp).getTime() >
        new Date(latest[key].timestamp).getTime()
    ) {
      latest[key] = p;
    }
  }
  return latest;
}

const BunkerPriceGrid: React.FC<BunkerPriceGridProps> = ({
  ports,
  prices,
  getPricesForPort,
  vessel,
  fromPort,
  toPort,
  eta,
  onClose,
}) => {
  const [portNotes, setPortNotes] = useState<{
    [unlocode: string]: TPortNote[];
  }>({});
  const [expandedPort, setExpandedPort] = useState<string | null>(null);
  const [weatherMap, setWeatherMap] = useState<Record<string, TWeather[]>>({});
  const [swellColorsMap, setSwellColorsMap] = useState<
    Record<string, string[]>
  >({});
  const [windColorsMap, setWindColorsMap] = useState<Record<string, string[]>>(
    {}
  );
  const apiClientRef = useRef<ApiClient | null>(null);
  const [requestForFirmPort, setRequestForFirmPort] = useState<string | null>(
    null
  );
  const [weatherDialogPort, setWeatherDialogPort] = useState<TPort | null>(
    null
  );

  useEffect(() => {
    // Load port notes once on mount
    (async () => {
      try {
        const apiClient = new ApiClient(config.apiBaseUrl || "");
        const notes = await apiClient.getPortNotes();
        // Group notes by unlocode
        const grouped: { [unlocode: string]: TPortNote[] } = {};
        for (const note of notes) {
          const u = note.unlocode?.toLowerCase();
          if (!u) continue;
          if (!grouped[u]) grouped[u] = [];
          grouped[u].push(note);
        }
        setPortNotes(grouped);
      } catch {
        setPortNotes({});
      }
    })();
    apiClientRef.current = new ApiClient(config.apiBaseUrl || "");
  }, []);

  // Deduplicate ports by unlocode
  const dedupedPorts = useMemo(() => {
    const seen = new Set<string>();
    return ports.filter((p) => {
      const u = p.unlocode.toLowerCase();
      if (seen.has(u)) return false;
      seen.add(u);
      return true;
    });
  }, [ports]);

  // Collect union of fuel grades across all ports (based on price list)
  const fuelGrades: string[] = useMemo(() => {
    const grades = new Set<string>();
    dedupedPorts.forEach((p) => {
      getPricesForPort(p.unlocode).forEach((price) =>
        grades.add(price.fuelGrade)
      );
    });
    return Array.from(grades).sort((a, b) => a.localeCompare(b));
  }, [dedupedPorts, getPricesForPort]);

  const latestPrices = useMemo(() => computeLatestPrices(prices), [prices]);

  // Sorting state
  const [sortCol, setSortCol] = useState<string>("port"); // 'unlocode' | 'port' | fuelGrade name
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  const sortedPorts = useMemo(() => {
    const list = [...dedupedPorts];
    list.sort((a, b) => {
      const uA = a.unlocode.toLowerCase();
      const uB = b.unlocode.toLowerCase();
      if (sortCol === "unlocode") {
        return uA.localeCompare(uB) * sortDir;
      }
      if (sortCol === "port") {
        const nA = a.name.toLowerCase();
        const nB = b.name.toLowerCase();
        return nA.localeCompare(nB) * sortDir;
      }
      // fuel grade column
      const keyA = `${uA}__${sortCol.toLowerCase()}`;
      const keyB = `${uB}__${sortCol.toLowerCase()}`;
      // Treat missing prices as very low so they appear at the bottom when sorting descending
      const sentinel = -1e15; // sufficiently low sentinel value
      const pA = latestPrices[keyA]?.price ?? sentinel;
      const pB = latestPrices[keyB]?.price ?? sentinel;
      if (pA === pB) return 0;
      return (pA < pB ? -1 : 1) * sortDir;
    });
    return list;
  }, [dedupedPorts, sortCol, sortDir, latestPrices]);

  // Sorting handler using functional updates
  const handleSort = (col: string) => {
    setSortCol((prevCol) => {
      if (prevCol === col) {
        setSortDir((d) => (d === 1 ? -1 : 1));
        return prevCol;
      }
      setSortDir(1);
      return col;
    });
  };

  // Weather fetch (swell + wind) for each port we actually have any price for
  useEffect(() => {
    const toFetch = dedupedPorts
      .filter((p) => getPricesForPort(p.unlocode).length > 0)
      .map((p) => p.unlocode.toLowerCase())
      .filter((u) => !weatherMap[u]);
    if (!toFetch.length) return;
    toFetch.forEach(async (u) => {
      const data = await getWeatherForPortCached(u);
      setWeatherMap((prev) => ({ ...prev, [u]: data }));
      setSwellColorsMap((prev) => ({
        ...prev,
        [u]: getForecastColorsFromWeatherHours(data, "swellHeight"),
      }));
      setWindColorsMap((prev) => ({
        ...prev,
        [u]: getForecastColorsFromWeatherHours(data, "windSpeed"),
      }));
    });
  }, [dedupedPorts, getPricesForPort, weatherMap]);

  const openRequestForFirm = (unlocode: string) => {
    setRequestForFirmPort(unlocode);
  };

  const closeRff = () => setRequestForFirmPort(null);

  // Build clusteredBunkerPrice input when RFF opened
  const rffCluster = useMemo(() => {
    if (!requestForFirmPort) return null;
    const portObj = dedupedPorts.find(
      (p) => p.unlocode.toLowerCase() === requestForFirmPort.toLowerCase()
    );
    if (!portObj) return null;
    const portPrices = getPricesForPort(portObj.unlocode);
    return {
      bunkerMarkers: [portObj],
      prices: portPrices,
      weather: new Map<string, TWeather[]>(),
    };
  }, [requestForFirmPort, dedupedPorts, getPricesForPort]);

  return (
    <div
      style={{
        position: "absolute",
        top: "auto",
        bottom: 0,
        right: 0,
        left: 0,
        height: "50vh", // lock to half of viewport height
        maxHeight: "50vh",
        background: "rgba(255,255,255,0.96)",
        zIndex: 15,
        backdropFilter: "blur(4px)",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "4px 12px",
          borderBottom: "1px solid #ddd",
        }}
      >
        <strong style={{ flex: 1 }}>Bunker Prices (Latest per Grade)</strong>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "#1e3e57",
            color: "#ffffff",
            border: "1px solid #1e3e57",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            minWidth: 72,
          }}
          title="Hide bunker price grid"
        >
          Hide
        </button>
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          fontSize: 12,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#f7f7f7",
              boxShadow: "0 2px 2px rgba(0,0,0,0.05)",
            }}
          >
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: 6,
                  cursor: "pointer",
                  width: 110, // widened to keep sort icon on same line
                  whiteSpace: "nowrap",
                }}
                onClick={() => handleSort("unlocode")}
              >
                UNLOCODE
                {sortCol === "unlocode" ? (sortDir === 1 ? " ▲" : " ▼") : ""}
              </th>
              <th
                style={{
                  textAlign: "left",
                  padding: 6,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  width: 160,
                }}
                onClick={() => handleSort("port")}
              >
                Port{sortCol === "port" ? (sortDir === 1 ? " ▲" : " ▼") : ""}
              </th>
              {fuelGrades.map((g) => {
                const colKey = g.toLowerCase();
                return (
                  <th
                    key={g}
                    style={{
                      textAlign: "right",
                      padding: 6,
                      cursor: "pointer",
                      width: 80,
                      minWidth: 80,
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleSort(colKey)}
                  >
                    {g.toUpperCase()}
                    {sortCol === colKey ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}
              <th style={{ padding: 4, textAlign: "center", width: 90 }}>
                Swell
              </th>
              <th style={{ padding: 4, textAlign: "center", width: 90 }}>
                Wind
              </th>
              <th style={{ padding: 6, width: 140 }} />
            </tr>
          </thead>
          <tbody>
            {sortedPorts.map((port, idx) => {
              // Get notes for this port
              const notesForPort = portNotes[port.unlocode.toLowerCase()] || [];
              const u = port.unlocode;
              const country = u.slice(0, 2).toUpperCase();
              const rowHasPrices = getPricesForPort(u).length > 0;
              if (!rowHasPrices) return null; // Skip ports without any price
              return (
                <tr
                  key={u}
                  style={{
                    borderBottom: "1px solid #c1ccd4",
                    background: idx % 2 === 0 ? "#ffffff" : "#d6e2eb", // bolder stripe
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      idx % 2 === 0 ? "#e3eef5" : "#c0d2de")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      idx % 2 === 0 ? "#ffffff" : "#d6e2eb")
                  }
                >
                  <td style={{ padding: 4, whiteSpace: "nowrap" }}>{u}</td>
                  <td style={{ padding: 4 }}>
                    {port.name} ({country})
                    {notesForPort.length > 0 && (
                      <div
                        style={{ marginTop: 2, fontSize: 11, color: "#d35400" }}
                      >
                        <strong>Note:</strong>{" "}
                        {(() => {
                          if (expandedPort === u) {
                            return (
                              <>
                                <span>
                                  {notesForPort
                                    .map((n, i) => `(${i + 1}) ${n.note}`)
                                    .join(" ")}
                                </span>
                                <a
                                  href="#"
                                  style={{ color: "#0a58ca", marginLeft: 4 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setExpandedPort(null);
                                  }}
                                >
                                  less
                                </a>
                              </>
                            );
                          }
                          // Show first 6 words of first note, with more link if more notes or longer
                          const firstNote = notesForPort[0]?.note || "";
                          const words = firstNote.split(" ");
                          if (notesForPort.length > 1 || words.length > 6) {
                            return (
                              <>
                                <span>
                                  {words.slice(0, 6).join(" ") +
                                    (words.length > 6 ? "..." : "")}
                                  {notesForPort.length > 1
                                    ? ` (+${notesForPort.length - 1} more)`
                                    : ""}
                                </span>
                                <a
                                  href="#"
                                  style={{ color: "#0a58ca", marginLeft: 4 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setExpandedPort(u);
                                  }}
                                >
                                  more
                                </a>
                              </>
                            );
                          }
                          return firstNote;
                        })()}
                      </div>
                    )}
                  </td>
                  {fuelGrades.map((g) => {
                    const key = `${u.toLowerCase()}__${g.toLowerCase()}`;
                    const latest = latestPrices[key];
                    return (
                      <td
                        key={g}
                        style={{
                          padding: 4,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          width: 80,
                        }}
                      >
                        {latest ? latest.price.toFixed(1) : "-"}
                      </td>
                    );
                  })}
                  <td style={{ padding: 2, textAlign: "center", width: 90 }}>
                    <div
                      role={
                        swellColorsMap[u.toLowerCase()]?.length
                          ? "button"
                          : undefined
                      }
                      onClick={() => {
                        const portObj = dedupedPorts.find(
                          (p) => p.unlocode.toLowerCase() === u.toLowerCase()
                        );
                        if (
                          portObj &&
                          (swellColorsMap[u.toLowerCase()] || []).length
                        )
                          setWeatherDialogPort(portObj);
                      }}
                      style={{
                        cursor: (swellColorsMap[u.toLowerCase()] || []).length
                          ? "pointer"
                          : "default",
                      }}
                      title="Click for 10-day weather forecast"
                    >
                      <ForecastStripe
                        colors={swellColorsMap[u.toLowerCase()] || []}
                        title="Swell height forecast"
                      />
                    </div>
                  </td>
                  <td style={{ padding: 2, textAlign: "center", width: 90 }}>
                    <div
                      role={
                        windColorsMap[u.toLowerCase()]?.length
                          ? "button"
                          : undefined
                      }
                      onClick={() => {
                        const portObj = dedupedPorts.find(
                          (p) => p.unlocode.toLowerCase() === u.toLowerCase()
                        );
                        if (
                          portObj &&
                          (windColorsMap[u.toLowerCase()] || []).length
                        )
                          setWeatherDialogPort(portObj);
                      }}
                      style={{
                        cursor: (windColorsMap[u.toLowerCase()] || []).length
                          ? "pointer"
                          : "default",
                      }}
                      title="Click for 10-day weather forecast"
                    >
                      <ForecastStripe
                        colors={windColorsMap[u.toLowerCase()] || []}
                        title="Wind speed forecast"
                      />
                    </div>
                  </td>
                  <td style={{ padding: 4 }}>
                    {vessel ? (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openRequestForFirm(u);
                        }}
                        style={{
                          color: "#0a58ca",
                          textDecoration: "underline",
                          fontSize: 12,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        title="Open Request for Firm"
                      >
                        Request for Firm...
                      </a>
                    ) : (
                      <span
                        style={{
                          color: "#888",
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          cursor: "not-allowed",
                          opacity: 0.6,
                        }}
                        title="Select a vessel first"
                      >
                        Request for Firm...
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {requestForFirmPort && rffCluster && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.98)",
            zIndex: 20,
            overflow: "auto",
          }}
        >
          <RequestForFirm
            clusteredBunkerPrice={rffCluster}
            vessel={vessel}
            fromPort={fromPort}
            toPort={toPort}
            toEta={eta}
            onCancel={closeRff}
            initialPort={requestForFirmPort}
          />
        </div>
      )}
      <PortWeatherForecast
        port={weatherDialogPort || null}
        weather={
          weatherDialogPort
            ? weatherMap[weatherDialogPort.unlocode.toLowerCase()]
            : undefined
        }
        open={!!weatherDialogPort}
        onClose={() => setWeatherDialogPort(null)}
      />
    </div>
  );
};

export default BunkerPriceGrid;
